import { omit, path, pick } from 'ramda'
import { gql, useQuery } from '@apollo/client'
import toInteger from 'lodash/toInteger'
import { useSelector } from 'react-redux'

import config from '@/config'
import { getSearch } from '@/utils/location'
import { useFilters } from '@/hooks/use-filters'
import BelmontPropertiesProfile from '@/features/Company/company-profile/belmont-properties'
import belmontPropertiesLogo from '@/features/Company/company-profile/belmont-properties/belmontproperties-logo.png'
import CarreraManagementCorporationProfile from '@/features/Company/company-profile/carrera-management-corporation'
import carreraManagementCorporationLogo from '@/features/Company/company-profile/carrera-management-corporation/carreramanagementcorporationlogo.png'
import CenturionPropertyAssociatesProfile from '@/features/Company/company-profile/centurion-property-associates'
import CenturionPropertyAssociatesLogo from '@/features/Company/company-profile/centurion-property-associates/logo.webp'
import CenturionPropertyAssociatesQr from '@/features/Company/company-profile/centurion-property-associates/qr-code.png'
import CLVGroupProfile from '@/features/Company/company-profile/clv-group'
import CLVGroupLogo from '@/features/Company/company-profile/clv-group/logo.png'
import ConcertRealtyServicesProfile from '@/features/Company/company-profile/concert-realty'
import ConcertRealtyServicesLogo from '@/features/Company/company-profile/concert-realty/concert-realty-logo.svg'
import DistrictRealtyProfile from '@/features/Company/company-profile/district-realty'
import DistrictRealtyLogo from '@/features/Company/company-profile/district-realty/logo.jpg'
import FirstarRealtyIncProfile from '@/features/Company/company-profile/firstarrealtyinc'
import firstarrealtyincLogo from '@/features/Company/company-profile/firstarrealtyinc/logo.jpg'
import GreenwinProfile from '@/features/Company/company-profile/greenwin'
import GreenwinLogo from '@/features/Company/company-profile/greenwin/logo.png'
import HomeLifePropertyManagementProfile from '@/features/Company/company-profile/homelife-property-management'
import HomeLifePropertyManagementLogo from '@/features/Company/company-profile/homelife-property-management/homelife_logo.png'
import InterRentProfile from '@/features/Company/company-profile/interrent'
import InterRentLogo from '@/features/Company/company-profile/interrent/logo.jpg'
import InterRentQr from '@/features/Company/company-profile/interrent/qr-code.png'
import LBrothersProfile from '@/features/Company/company-profile/l-brothers'
import lBrothersLogoBlk from '@/features/Company/company-profile/l-brothers/lbrothers-logo-blk.png'
import lBrothersQr from '@/features/Company/company-profile/l-brothers/lbrothers-qrcode.png'
import MainStreetEquityCorpProfile from '@/features/Company/company-profile/mainstreet-equity-corp'
import MainStreetEquityCorpLogo from '@/features/Company/company-profile/mainstreet-equity-corp/logo.jpg'
import MainStreetEquityCorpQr from '@/features/Company/company-profile/mainstreet-equity-corp/qr-code.png'
import MetcapLivingProfile from '@/features/Company/company-profile/metcap-living'
import MetcapLivingLogo from '@/features/Company/company-profile/metcap-living/logo.png'
import MetcapLivingQr from '@/features/Company/company-profile/metcap-living/qr-code.png'
import PowerPropertiesProfile from '@/features/Company/company-profile/power-properties'
import PowerPropertiesLogo from '@/features/Company/company-profile/power-properties/logo.jpg'
import PowerPropertiesQr from '@/features/Company/company-profile/power-properties/qr-code.png'
import promptonProfile from '@/features/Company/company-profile/prompton'
import promptonLogo from '@/features/Company/company-profile/prompton/prompton-logo.png'
import promptonQr from '@/features/Company/company-profile/prompton/prompton-qrcode.png'
import RealstarProfile from '@/features/Company/company-profile/realstar'
import RealstarLogo from '@/features/Company/company-profile/realstar/logo.png'
import SterlingKaramarProfile from '@/features/Company/company-profile/sterling-karamar'
import SterlingKaramarLogo from '@/features/Company/company-profile/sterling-karamar/sterling-karamar-logo.png'
import SterlingKaramarQR from '@/features/Company/company-profile/sterling-karamar/sterling-karamar-qr-code.png'
import UooiPropertyManagementProfile from '@/features/Company/company-profile/uooi-property-management'
import uooiPropertyManagementLogo from '@/features/Company/company-profile/uooi-property-management/uooipropertymanagement-logo.png'
import VancouverPropertyManagementProfile from '@/features/Company/company-profile/vancouver-property-management'
import RemaxPropertyManagementLogo from '@/features/Company/company-profile/vancouver-property-management/remax-crest-realty.jpeg'
import VancouverPropertyManagementLogo from '@/features/Company/company-profile/vancouver-property-management/vanpmmaster_logo.png'
import VantageWestRealtyProfile from '@/features/Company/company-profile/vantage-west-realty'
import VantageWestRealtyLogo from '@/features/Company/company-profile/vantage-west-realty/vantage-west-realty-logo.png'
import WBrothersProfile from '@/features/Company/company-profile/w-brothers'
import wBrothersLogo from '@/features/Company/company-profile/w-brothers/wbrothers-logo.png'
import { GatherColivingSeo } from '@/features/Company/seo'

const isDev = config.BUILD_ENV === 'dev'

const PROFILES = {
  lbrothers: {
    id: isDev ? 100000725 : 25332,
    pageType: 'vip',
    url: 'lbrothers',
    qr: lBrothersQr,
    qrAlt: 'L Brothers QR Code',
    logoAlt: 'L Brothers Real Estate Team',
    logo: lBrothersLogoBlk,
    profile: LBrothersProfile,
    showRented: true
  },
  prompton: {
    id: isDev ? 168 : 10,
    pageType: 'vip',
    url: 'prompton',
    qr: promptonQr,
    qrAlt: 'Prompton QR Code',
    logoAlt: 'Prompton Real Estate Inc.',
    logo: promptonLogo,
    profile: promptonProfile,
    showRented: true
  },
  wbrothers: {
    id: isDev ? 235 : 817,
    pageType: 'vip',
    url: 'wbrothers',
    name: 'Kevin Wang Team',
    logoAlt: 'W Brothers Real Estate Team',
    logo: wBrothersLogo,
    profile: WBrothersProfile,
    showRented: true
  },
  uooipropertymanagement: {
    id: isDev ? 1 : 394,
    pageType: 'vip',
    url: 'uooipropertymanagement',
    logoAlt: 'Uooi Property Management Company',
    logo: uooiPropertyManagementLogo,
    profile: UooiPropertyManagementProfile,
    showRented: true
  },
  carreramanagementcorporation: {
    id: isDev ? 3 : 163261,
    pageType: 'vip',
    url: 'carreramanagementcorporation',
    logoAlt: 'Carrera Management Corporation',
    logo: carreraManagementCorporationLogo,
    profile: CarreraManagementCorporationProfile,
    showRented: true
  },
  belmontproperties: {
    id: isDev ? 17 : 148513,
    pageType: 'vip',
    url: 'belmontproperties',
    logoAlt: 'Belmont Properties',
    logo: belmontPropertiesLogo,
    profile: BelmontPropertiesProfile,
    showRented: true
  },
  gathercoliving: {
    id: isDev ? 14 : 28356,
    pageType: 'custom',
    url: 'gathercoliving',
    name: 'Gather Co-living',
    seoTitle: `Find private rooms for rent in Vancouver offered by Gather Coliving - Vancouver's #1 coliving operator. Search rental listings currently available on liv.rent`,
    seoImageAlt: 'gather coliving rooms for rent vancouver',
    seoComponent: GatherColivingSeo,
    showRented: true
  },
  sterlingkaramar: {
    id: isDev ? 1 : 46467,
    pageType: 'vip',
    url: 'sterlingkaramar',
    siteUrl: 'https://www.sterlingkaramar.com',
    customSize: {
      height: '100px',
      width: '200px',
      mobileHeight: '75px',
      mobileWidth: '150px'
    },
    name: 'Sterling Karamar Property Management',
    logoAlt: 'Sterling Karamar Property Management Logo',
    logo: SterlingKaramarLogo,
    qr: SterlingKaramarQR,
    profile: SterlingKaramarProfile,
    showRented: false
  },
  concertrealtyservices: {
    id: isDev ? 1 : 11286,
    pageType: 'vip',
    url: 'concertrealtyservices',
    siteUrl: 'https://www.concertproperties.com/rentals',
    customSize: {
      height: '300px',
      width: '275px',
      mobileHeight: '175px',
      mobileWidth: '150px'
    },
    name: 'Concert Realty Services',
    logoAlt: 'Concert Realty Services Logo',
    logo: ConcertRealtyServicesLogo,
    profile: ConcertRealtyServicesProfile,
    showRented: true
  },
  vancouverpropertymanagementmaster: {
    id: isDev ? 1 : 126786,
    pageType: 'vip',
    url: 'vancouverpropertymanagementmaster',
    customSize: {
      height: '52px',
      width: '130px',
      mobileHeight: '30px',
      mobileWidth: '75px'
    },
    name: 'Vancouver Property Management',
    logoAlt: 'Vancouver Property Management Logo',
    logo: VancouverPropertyManagementLogo,
    secondaryLogo: RemaxPropertyManagementLogo,
    profile: VancouverPropertyManagementProfile,
    showRented: true
  },
  vantagewestrealty: {
    id: isDev ? 1 : 190858,
    pageType: 'vip',
    url: 'vantagewestrealty',
    siteUrl: 'https://www.propertymanagementkelowna.com/',
    customSize: {
      height: '60px',
      width: '226px',
      mobileHeight: '40px',
      mobileWidth: '150px'
    },
    name: 'Vantage West Realty',
    logoAlt: 'Vantage West Realty Logo',
    logo: VantageWestRealtyLogo,
    profile: VantageWestRealtyProfile,
    showRented: true
  },
  emmypang: {
    id: isDev ? 100005902 : 17757,
    pageType: 'custom',
    url: 'emmypang',
    name: 'Emmy Pang ABC Realty',
    showRented: true
  },
  firststaybc: {
    id: isDev ? 100005900 : 928,
    pageType: 'custom',
    url: 'firststaybc',
    name: 'First Stay BC',
    showRented: true
  },
  carolynchen: {
    id: isDev ? 168 : 6263,
    pageType: 'custom',
    url: 'carolynchen',
    name: 'Carolyn Chen',
    showRented: true
  },
  ramongutierrez: {
    id: isDev ? 168 : 1221,
    pageType: 'custom',
    url: 'ramongutierrez',
    name: 'Ramon Guiterrez',
    showRented: true
  },
  metcapliving: {
    id: isDev ? 100009917 : 9859,
    pageType: 'vip',
    url: 'metcapliving',
    siteUrl: 'https://www.metcap.com/',
    qr: MetcapLivingQr,
    qrAlt: 'MetCap Living QR Code',
    customSize: {
      height: '75px',
      width: '75px',
      mobileHeight: '45px',
      mobileWidth: '45px'
    },
    name: 'Metcap Living',
    logoAlt: 'Metcap Living Logo',
    logo: MetcapLivingLogo,
    profile: MetcapLivingProfile,
    showRented: false
  },
  mainstreetequitycorp: {
    id: isDev ? 100010439 : 238826,
    pageType: 'vip',
    url: 'mainstreetequitycorp',
    siteUrl: 'https://www.mainst.biz/',
    qr: MainStreetEquityCorpQr,
    qrAlt: 'Main Street Equity Corp. QR Code',
    customSize: {
      height: '75px',
      width: '75px',
      mobileHeight: '45px',
      mobileWidth: '45px'
    },
    name: 'Main Street Equity Corp.',
    logoAlt: 'Main Street Equity Corp. Logo',
    logo: MainStreetEquityCorpLogo,
    profile: MainStreetEquityCorpProfile,
    showRented: false
  },
  centurionpropertyassociates: {
    id: isDev ? 100010661 : 239564,
    pageType: 'vip',
    url: 'centurionpropertyassociates',
    siteUrl: 'https://www.cpliving.com/',
    qr: CenturionPropertyAssociatesQr,
    qrAlt: 'Centurion Property Associates Inc. QR Code',
    customSize: {
      height: '100px',
      width: '100px',
      mobileHeight: '55px',
      mobileWidth: '55px'
    },
    name: 'Centurion Property Associates Inc.',
    logoAlt: 'Centurion Property Associates Inc. Logo',
    logo: CenturionPropertyAssociatesLogo,
    profile: CenturionPropertyAssociatesProfile,
    showRented: false
  },
  homelifepropertymanagementbc: {
    id: isDev ? 1 : 94737,
    pageType: 'vip',
    url: 'homelifepropertymanagementbc',
    siteUrl: 'https://homelifepropertyrentals.ca/',
    customSize: {
      height: '75px',
      width: '75px',
      mobileHeight: '45px',
      mobileWidth: '45px'
    },
    name: 'HomeLife Property Management',
    logoAlt: 'HomeLife Property Management Logo',
    logo: HomeLifePropertyManagementLogo,
    profile: HomeLifePropertyManagementProfile,
    showRented: true
  },
  interrent: {
    id: isDev ? 100010723 : 240525,
    pageType: 'vip',
    url: 'interrent',
    siteUrl: 'https://www.irent.com/en/',
    qr: InterRentQr,
    qrAlt: 'InterRent QR Code',
    customSize: {
      height: '75px',
      width: '75px',
      mobileHeight: '45px',
      mobileWidth: '45px'
    },
    name: 'InterRent',
    logoAlt: 'InterRent Logo',
    logo: InterRentLogo,
    profile: InterRentProfile,
    showRented: false
  },
  clvgroup: {
    id: isDev ? 100010723 : 281810,
    pageType: 'vip',
    url: 'clvgroup',
    siteUrl: 'https://www.clvgroup.com/apartment-rentals/',
    customSize: {
      height: '75px',
      width: '75px',
      mobileHeight: '45px',
      mobileWidth: '45px'
    },
    name: 'CLV Group',
    logoAlt: 'CLV Group Logo',
    logo: CLVGroupLogo,
    profile: CLVGroupProfile,
    showRented: false
  },
  districtrealty: {
    id: isDev ? 100010723 : 221906,
    pageType: 'vip',
    url: 'districtrealty',
    siteUrl: 'https://www.districtrealty.com/ottawa-apartments/for-rent/',
    logoAlt: 'District Realty Logo',
    logo: DistrictRealtyLogo,
    profile: DistrictRealtyProfile,
    showRented: true
  },
  greenwin: {
    id: isDev ? 100009917 : 247067,
    pageType: 'vip',
    url: 'greenwin',
    siteUrl: 'https://www.greenwin.ca/',
    customSize: {
      height: '75px',
      width: '75px',
      mobileHeight: '45px',
      mobileWidth: '45px'
    },
    logo: GreenwinLogo,
    logoAlt: 'Greenwin Logo',
    profile: GreenwinProfile,
    showRented: false
  },
  realstarcorp: {
    id: isDev ? 100010723 : 221185,
    pageType: 'vip',
    url: 'realstarcorp',
    siteUrl: 'https://www.realstar.ca/',
    customSize: {
      height: '75px',
      width: '75px',
      mobileHeight: '45px',
      mobileWidth: '45px'
    },
    name: 'Realstar Corp',
    logoAlt: 'Realstar Logo',
    logo: RealstarLogo,
    profile: RealstarProfile,
    showRented: true
  },
  firstarrealtyinc: {
    id: isDev ? 100010723 : 193156,
    pageType: 'vip',
    url: 'firstarrealtyinc',
    siteUrl: 'https://www.firstarrealty.com/',
    customSize: {
      height: '60px',
      width: '60px',
      mobileHeight: '40px',
      mobileWidth: '40px'
    },
    name: 'Firstar Realty Corp.',
    logoAlt: 'Firstar Realty Corp. Logo',
    logo: firstarrealtyincLogo,
    profile: FirstarRealtyIncProfile,
    showRented: true
  },
  powerproperties: {
    id: isDev ? 100010661 : 271787,
    pageType: 'vip',
    url: 'powerproperties',
    siteUrl: 'https://www.powerproperties.net/',
    qr: PowerPropertiesQr,
    qrAlt: 'Power Properties QR Code',
    customSize: {
      height: '70px',
      width: '70px',
      mobileHeight: '40px',
      mobileWidth: '40px'
    },
    name: 'Power Properties',
    logoAlt: 'Power Properties Logo',
    logo: PowerPropertiesLogo,
    profile: PowerPropertiesProfile,
    showRented: false
  }
}

export const COMPANY_DATA_QUERY = gql`
  fragment brokerInfo on FullListing {
    broker {
      name
    }
    broker_files {
      tag
      location {
        key
      }
    }
  }

  fragment companyListViewFields on FullListing {
    street_name
    building_type
    building_name
    full_street_name
    city
    state
    zip
    location {
      lat
      lon
    }
    bathrooms
    bedrooms
    size
    gr_min_price
    gr_max_price
    gr_unit
    hide_unit_number
    unit_type_txt_id
    unit_type_scope_txt_id
    unit_subtype_txt_id
    cover_photo_aws_s3_key
    listing_id
    price
    price_frequency
    verified_state_machine
    is_hidden
    state_machine
    search_deals
    previous_price
    landlords {
      identity_verified_state
    }
    allow_applications
    public_viewing
    unit_files {
      aws_s3_key
      position
      tag
      description
    }
  }
  query company($input: CompanyListSearchInput!, $featInput: ListSearchInput!) {
    companyListSearch {
      companyListings(input: $input) {
        metadata {
          page_size
          page
          total_count
        }
        feed {
          ...companyListViewFields
          ...brokerInfo
        }
        externalInfo {
          realtor_name
          realtor_external_id
          realtor_logo_url
          realtor_display_info
        }
      }
      featured(input: $featInput) {
        ...companyListViewFields
      }
    }
  }
`

export function useCompanyData(companyId) {
  return useSelector(path(['company', companyId]))
}

export function useCompanyProfile(companyId) {
  return PROFILES[companyId]
}

export function getCompanyProfileById(companyId) {
  const profiles = Object.values(PROFILES)
  const profile = profiles.find(
    ({ id, url }) => `${id}` === companyId || url === companyId
  )
  return profile
}

const getFeaturedFilter = filters => {
  const standard = { featured: 3, cities: [] }
  const user = pick(
    [
      'page',
      'page_size',
      'housing_types',
      'unit_types',
      'bedroom_count',
      'furnished'
    ],
    filters
  )
  return { ...standard, ...user }
}

export const getVariables = filters => {
  return {
    input: omit(['cities', 'featured'], filters),
    featInput: getFeaturedFilter(filters)
  }
}

export function useCompanyListingsData(companyId, extCompanyId) {
  const [originalFilters] = useFilters()

  const filters = {
    state_machine: 'published',
    company_id: toInteger(companyId),
    sub_company_id: extCompanyId || null,
    ...originalFilters,
    cities: null
  }

  const {
    data = {},
    error,
    client,
    loading
  } = useQuery(COMPANY_DATA_QUERY, {
    variables: getVariables(filters),
    fetchPolicy: 'cache-first'
  })

  const preload = newFilter => {
    // Use cache-first to avoid unnecessary network requests if data is already cached
    client.query({
      query: COMPANY_DATA_QUERY,
      variables: getVariables({ ...filters, ...newFilter }),
      fetchPolicy: 'cache-first'
    })
  }

  return { data, error, loading, preload, filters }
}

export async function getCompanyData({
  req,
  query,
  params,
  reduxStore,
  apolloClient
}) {
  const { dispatch } = reduxStore

  const id = params?.id
  const companyProfile = PROFILES[id]
  const companyId = companyProfile?.id || id
  const extCompanyId = query?.ext || null
  await dispatch.company.load(companyId, req)
  const search = getSearch({ query }, { setDefaultCity: false })
  dispatch.listings.updateCompanyFilter({
    sort: { sort_by: 'CREATED_AT', sort_type: 'DESC' },
    ...search,
    cities: null
  })

  const originalFilters = reduxStore.getState().listings.filters
  const filters = {
    state_machine: 'published',
    company_id: toInteger(companyId),
    sub_company_id: extCompanyId,
    ...originalFilters,
    cities: null
  }

  // Await the Apollo query to ensure data is loaded server-side
  await apolloClient.query({
    query: COMPANY_DATA_QUERY,
    variables: getVariables(filters)
  })
}
